import { IDVerificationType } from "@/types/IDVerificationType";
import { useMemo } from "react";
import useSWR from "@/hooks/useSWR";
import { useVerificationFlow } from "./useVerificationFlow";
import { canRetryIdVerification } from "@/components/IdVerification/helpers";
import useUseCaseVerificationStatus from "./useUseCaseVerificationStatus";
import usePaymentVerificationStatus from "./usePaymentVerificationStatus";

export default function useNeedsAccountVerification() {
  const { data: verification, isLoading: idVerificationLoading } =
    useSWR<IDVerificationType>("/idverification/");

  const { data: { results: flows } = {}, isLoading: areFlowsLoading } =
    useVerificationFlow({
      flowType__in: ["acceptable_use_violation", "fraudulent_payment"],
      state: "inflow",
    });

  const { status: useCaseVerificationStatus } = useUseCaseVerificationStatus();
  const { status: paymentVerificationStatus } = usePaymentVerificationStatus();

  const hasUnansweredQuestions =
    useCaseVerificationStatus === "additional-questions" ||
    paymentVerificationStatus === "additional-questions";

  const needsEvidence = useMemo(
    () => !!flows?.some((item) => item.needs_evidence),
    [flows]
  );

  const { state } = verification || {};

  const needsIdVerification = useMemo(
    () =>
      flows?.filter((item) => item.id_verification_required)?.length &&
      state !== "verified",
    [flows, state]
  );

  const shouldRetryIdVerification = useMemo(() => {
    if (!verification) return false;
    if (
      canRetryIdVerification(verification) &&
      verification.state === "failed"
    ) {
      return true;
    }
    return false;
  }, [verification]);

  const needsAnyVerification =
    needsEvidence ||
    (Number(flows?.length) > 0 && hasUnansweredQuestions) ||
    needsIdVerification;

  const canVerifyId =
    (state && !["not-required", "verified"].includes(state)) ||
    needsAnyVerification;

  return {
    needsEvidence,
    hasUnansweredQuestions,
    needsIdVerification,
    needsAnyVerification,
    flows,
    isLoading: idVerificationLoading || areFlowsLoading,
    shouldRetryIdVerification: shouldRetryIdVerification && needsIdVerification,
    canVerifyId,
  };
}
